<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { pageByPath, MENU_GROUPS, PAGE_DEFINITIONS } from '@/data/registry'
import { usePrototypeStore } from '@/stores/prototype'
import { useTaskStore } from '@/stores/tasks'
import AppIcon from '@/components/AppIcon.vue'

const route = useRoute()
const prototype = usePrototypeStore()
const tasks = useTaskStore()
const tab = ref<'elements' | 'current' | 'shell' | 'tasks'>('elements')
const collapsed = ref(false)
const page = computed(() => pageByPath(route.path))
const taskRows = computed(() => tasks.recent.slice(0, 5))
const totalPages = computed(() => PAGE_DEFINITIONS.length)
/** project.json fixes 31 business pages and 7 foundation pages. */
const FOUNDATION_PAGE_IDS = new Set(['login', 'profile', 'forbidden', 'not-found', 'server-error', 'maintenance', 'offline'])
const businessPages = computed(() => PAGE_DEFINITIONS.filter(item => !FOUNDATION_PAGE_IDS.has(item.id)).length)
const menuGroups = computed(() => MENU_GROUPS.length)

const TASK_STATUS: Record<string, string> = {
  queued: '排队中',
  running: '进行中',
  success: '已完成',
  partial_success: '部分失败',
  failed: '已失败',
  cancelled: '已取消',
  排队中: '排队中',
  进行中: '进行中',
  已完成: '已完成',
  部分失败: '部分失败',
  已失败: '已失败',
  已取消: '已取消',
}

function taskStatusLabel(value: unknown): string {
  return TASK_STATUS[String(value)] ?? '待处理'
}

const tabs = [
  { key: 'elements', label: '页面元素', icon: 'layers' },
  { key: 'current', label: '当前页面', icon: 'compass' },
  { key: 'shell', label: '整个后台', icon: 'grid' },
  { key: 'tasks', label: '任务进度', icon: 'task' },
] as const

function close(): void {
  prototype.setDevMode(false)
}
</script>

<template>
  <aside class="dev-panel" :class="{ 'is-collapsed': collapsed }" aria-label="原型开发模式">
    <header class="dev-panel__head">
      <span class="dev-panel__mark"><AppIcon name="eye" :size="13" /></span>
      <div>
        <b>原型开发模式</b>
        <small>仅当前浏览器 · 超级管理员</small>
      </div>
      <button type="button" class="dev-panel__icon" :aria-label="collapsed ? '展开面板' : '收起面板'" @click="collapsed = !collapsed"><AppIcon :name="collapsed ? 'chevron-left' : 'chevron-right'" :size="14" /></button>
      <button type="button" class="dev-panel__icon" aria-label="退出原型开发模式" @click="close"><AppIcon name="close" :size="14" /></button>
    </header>
    <div v-if="!collapsed" class="dev-panel__body">
      <div class="dev-panel__tabs" role="tablist" aria-label="开发模式范围">
        <button v-for="item in tabs" :key="item.key" type="button" role="tab" :aria-selected="tab === item.key" :class="{ 'is-active': tab === item.key }" @click="tab = item.key">
          <AppIcon :name="item.icon" :size="12" />{{ item.label }}
        </button>
      </div>

      <div v-if="tab === 'elements'" class="dev-panel__section">
        <p class="dev-panel__hint">当前页面定义中的可见元素，不包含正式接口或数据状态。</p>
        <ul class="dev-list">
          <li><span>页面标题</span><b>{{ page?.title || '—' }}</b></li>
          <li><span>列表列</span><b>{{ page?.columns.length || 0 }} 个</b></li>
          <li><span>筛选项</span><b>{{ page?.filters.length || 0 }} 个</b></li>
          <li><span>汇总指标</span><b>{{ page?.summary?.length || 0 }} 个</b></li>
          <li><span>表单字段</span><b>{{ page?.formFields?.length || 0 }} 个</b></li>
          <li><span>行内操作</span><b>{{ page?.rowActions?.length || 0 }} 个</b></li>
        </ul>
      </div>

      <div v-if="tab === 'current'" class="dev-panel__section">
        <p class="dev-panel__hint">当前路由与页面语义，刷新后仍可直接进入。</p>
        <ul class="dev-list">
          <li><span>当前路由</span><b>{{ route.path }}</b></li>
          <li><span>页面名称</span><b>{{ page?.title || '未知页面' }}</b></li>
          <li><span>所属分组</span><b>{{ page?.group || '—' }}</b></li>
          <li><span>页面权限</span><b>{{ page?.permission || '—' }}</b></li>
          <li><span>页面类型</span><b>{{ page?.kind || '—' }}</b></li>
        </ul>
      </div>

      <div v-if="tab === 'shell'" class="dev-panel__section">
        <p class="dev-panel__hint">当前 Vue 原型的后台信息架构与页面覆盖范围。</p>
        <div class="dev-metrics">
          <span><b>{{ totalPages }}</b><small>注册页面</small></span>
          <span><b>{{ businessPages }}</b><small>业务页面</small></span>
          <span><b>{{ menuGroups }}</b><small>导航分组</small></span>
        </div>
        <ul class="dev-list">
          <li><span>响应式宽度</span><b>1440 / 1280 / 1024</b></li>
          <li><span>当前数据模式</span><b>可复现内存数据</b></li>
          <li><span>导航与路由</span><b>同一注册表</b></li>
          <li><span>面包屑与标签</span><b>随路由同步</b></li>
        </ul>
      </div>

      <div v-if="tab === 'tasks'" class="dev-panel__section">
        <p class="dev-panel__hint">当前浏览器内的实时任务进度，不连接后台队列。</p>
        <div v-if="taskRows.length" class="dev-tasks">
          <div v-for="task in taskRows" :key="task.id" class="dev-task">
            <div class="dev-task__top"><b>{{ task.title }}</b><span>{{ taskStatusLabel(task.status) }}</span></div>
            <div class="dev-task__bar"><i :style="{ width: `${task.progress}%` }" /></div>
            <div class="dev-task__foot"><span>{{ task.success }} / {{ task.total }}</span><span>{{ task.progress }}%</span></div>
          </div>
        </div>
        <div v-else class="dev-panel__empty">当前没有进行中的原型任务。</div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.dev-panel { position: fixed; right: 16px; bottom: 16px; z-index: 1200; width: 334px; border: 1px solid #cfe6dc; border-radius: 14px; background: #fff; box-shadow: 0 16px 44px rgba(7,26,67,.16); overflow: hidden; }
.dev-panel.is-collapsed { width: 220px; }
.dev-panel__head { display: flex; align-items: center; gap: 7px; padding: 9px 10px; background: linear-gradient(135deg, #0f3b2e, #116b50); color: #fff; }
.dev-panel__mark { width: 24px; height: 24px; border-radius: 7px; display: grid; place-items: center; background: rgba(255,255,255,.14); }
.dev-panel__head > div { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.dev-panel__head b { font-size: 10.5px; }
.dev-panel__head small { margin-top: 1px; font-size: 8px; color: rgba(255,255,255,.65); }
.dev-panel__icon { width: 22px; height: 22px; display: grid; place-items: center; border: 0; border-radius: 6px; background: transparent; color: rgba(255,255,255,.75); }
.dev-panel__icon:hover { background: rgba(255,255,255,.15); color: #fff; }
.dev-panel__body { padding: 8px 10px 10px; }
.dev-panel__tabs { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 3px; padding: 3px; border-radius: 8px; background: #f3f7f5; }
.dev-panel__tabs button { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 5px 2px; border: 0; border-radius: 6px; background: transparent; color: #7183a1; font-size: 8.5px; }
.dev-panel__tabs button.is-active { background: #fff; color: #077e58; font-weight: 600; box-shadow: 0 1px 2px rgba(7,26,67,.08); }
.dev-panel__section { margin-top: 8px; }
.dev-panel__hint { margin: 0 0 7px; font-size: 8.5px; line-height: 1.5; color: #8b9aad; }
.dev-list { display: grid; gap: 0; margin: 0; padding: 0; list-style: none; border: 1px solid #edf2f0; border-radius: 8px; overflow: hidden; }
.dev-list li { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-top: 1px solid #f1f5f3; font-size: 9.5px; }
.dev-list li:first-child { border-top: 0; }
.dev-list span { color: #8b9aad; }
.dev-list b { margin-left: auto; max-width: 62%; text-align: right; color: #1f2d45; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dev-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 5px; margin-bottom: 7px; }
.dev-metrics span { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 7px 4px; border-radius: 8px; background: #f1fbf6; border: 1px solid #d9f0e6; }
.dev-metrics b { font-family: var(--font-num); font-size: 16px; color: #067d57; line-height: 1; }
.dev-metrics small { font-size: 8px; color: #5f8a7a; }
.dev-tasks { display: grid; gap: 6px; }
.dev-task { padding: 7px; border: 1px solid #edf2f0; border-radius: 8px; }
.dev-task__top { display: flex; gap: 8px; font-size: 9px; color: #1f2d45; }
.dev-task__top b { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dev-task__top span { color: #077e58; font-size: 8px; }
.dev-task__bar { height: 4px; margin: 5px 0 3px; border-radius: 3px; background: #e8f1ed; overflow: hidden; }
.dev-task__bar i { display: block; height: 100%; border-radius: 3px; background: #08b578; transition: width .3s ease; }
.dev-task__foot { display: flex; justify-content: space-between; font-size: 8px; color: #8b9aad; }
.dev-panel__empty { padding: 14px 8px; border: 1px dashed #dce9e3; border-radius: 8px; text-align: center; font-size: 9px; color: #8b9aad; }
@media (max-width: 767px) { .dev-panel { right: 8px; bottom: 8px; width: min(334px, calc(100vw - 16px)); } }
</style>
